/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { EditorCardProvider } from '@atlaskit/editor-card-provider/provider';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import { toJSON } from '@atlaskit/editor-common/utils';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { cardPlugin } from '@atlaskit/editor-plugins/card';
import type { CardPluginOptions } from '@atlaskit/editor-plugins/card';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugins/clear-marks-on-empty-doc';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { copyButtonPlugin } from '@atlaskit/editor-plugins/copy-button';
import { decorationsPlugin } from '@atlaskit/editor-plugins/decorations';
import { editorDisabledPlugin } from '@atlaskit/editor-plugins/editor-disabled';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugins/floating-toolbar';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { gridPlugin } from '@atlaskit/editor-plugins/grid';
import { historyPlugin } from '@atlaskit/editor-plugins/history';
import { hyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { selectionPlugin } from '@atlaskit/editor-plugins/selection';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import { undoRedoPlugin } from '@atlaskit/editor-plugins/undo-redo';
import { userIntentPlugin } from '@atlaskit/editor-plugins/user-intent';
import { widthPlugin } from '@atlaskit/editor-plugins/width';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { token } from '@atlaskit/tokens';

const promptEditorStyles = css({
	backgroundColor: token('color.background.input'),
	borderColor: token('color.border.input'),
	borderRadius: token('radius.small'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.100'),
	'&:focus-within': {
		borderColor: token('color.border.focused'),
		boxShadow: `0 0 0 ${token('border.width.focused')} ${token('color.border.focused')}`,
	},
});

const promptEditorInvalidStyles = css({
	borderColor: token('color.border.danger'),
	'&:focus-within': {
		borderColor: token('color.border.danger'),
		boxShadow: `0 0 0 ${token('border.width.focused')} ${token('color.border.danger')}`,
	},
});

const promptEditorDisabledStyles = css({
	backgroundColor: token('color.background.disabled'),
	borderColor: token('color.border.disabled'),
});

export type ManagedBlockPromptEditorProps = {
	ariaDescribedBy?: string;
	ariaLabel: string;
	autoFocus: boolean;
	defaultValue?: JSONDocNode;
	isDisabled?: boolean;
	isInvalid?: boolean;
	onADFChange: (input: JSONDocNode) => void;
	onInputChange: (input: string) => void;
	placeholder: string;
	setFocusHandler?: (focusFn?: () => boolean) => void;
};

const createManagedBlockPromptPreset = ({
	cardProvider,
	placeholder,
}: {
	cardProvider: EditorCardProvider;
	placeholder: string;
}) => {
	const cardPluginData: CardPluginOptions = {
		provider: Promise.resolve(cardProvider),
		disableFloatingToolbar: true,
		showUpgradeDiscoverability: false,
		allowBlockCards: false,
		allowEmbeds: false,
	};

	return new EditorPresetBuilder()
		.add(betterTypeHistoryPlugin)
		.add([pastePlugin, {}])
		.add(clipboardPlugin)
		.add(focusPlugin)
		.add(compositionPlugin)
		.add([basePlugin, {}])
		.add(decorationsPlugin)
		.add(typeAheadPlugin)
		.add(historyPlugin)
		.add(undoRedoPlugin)
		.add(clearMarksOnEmptyDocPlugin)
		.add([hyperlinkPlugin, { disableFloatingToolbar: true }])
		.add(widthPlugin)
		.add([placeholderPlugin, { placeholder }])
		.add(editorDisabledPlugin)
		.add(copyButtonPlugin)
		.add(floatingToolbarPlugin)
		.add([selectionPlugin, {}])
		.add([gridPlugin, {}])
		.add([cardPlugin, cardPluginData])
		.add(userIntentPlugin);
};

const ManagedBlockPromptEditorInner = ({
	ariaDescribedBy,
	ariaLabel,
	autoFocus,
	defaultValue,
	isDisabled = false,
	isInvalid = false,
	onADFChange,
	onInputChange,
	placeholder,
	setFocusHandler,
}: ManagedBlockPromptEditorProps): JSX.Element => {
	const cardProvider = useMemo(() => new EditorCardProvider(), []);
	const textSerializerRef = useRef<{
		schema: EditorView['state']['schema'];
		serializer: ReturnType<typeof TextSerializer.fromSchema>;
	} | null>(null);
	const { preset, editorApi } = usePreset(
		() => createManagedBlockPromptPreset({ cardProvider, placeholder }),
		[cardProvider, placeholder],
	);

	const handleChange = useCallback(
		(editorView: EditorView) => {
			const { schema } = editorView.state;
			if (textSerializerRef.current?.schema !== schema) {
				textSerializerRef.current = {
					schema,
					serializer: TextSerializer.fromSchema(schema),
				};
			}

			const textSerializer = textSerializerRef.current.serializer;
			const text = textSerializer.serializeFragment(editorView.state.doc.content);
			const adf = toJSON(editorView.state.doc);

			onADFChange(adf);
			onInputChange(text);
		},
		[onADFChange, onInputChange],
	);

	useEffect(() => {
		setFocusHandler?.(editorApi?.core?.actions.focus);
	}, [editorApi, setFocusHandler]);

	return (
		<div
			aria-disabled={isDisabled}
			css={[
				promptEditorStyles,
				isInvalid && promptEditorInvalidStyles,
				isDisabled && promptEditorDisabledStyles,
			]}
			data-testid="agent-managed-extension-prompt-input-field"
		>
			<ComposableEditor
				appearance="chromeless"
				assistiveDescribedBy={ariaDescribedBy}
				assistiveLabel={ariaLabel}
				defaultValue={defaultValue}
				disabled={isDisabled}
				minHeight={100}
				onChange={handleChange}
				preset={preset}
				shouldFocus={autoFocus}
			/>
		</div>
	);
};

export const ManagedBlockPromptEditor: (props: ManagedBlockPromptEditorProps) => JSX.Element = (
	props,
) => (
	<EditorContext>
		<ManagedBlockPromptEditorInner
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
		/>
	</EditorContext>
);
